import React, { useState, useEffect } from 'react';
import useLatestCallback from '@/hooks/useLatestCallback';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, Search, Edit, Trash2, Building, Phone, Mail, ArrowLeft } from 'lucide-react';
import { BO_BASE } from '@/utils/getBasePath';
const bo = (route = '') => route ? `${BO_BASE}/${route}` : (BO_BASE || '/');

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function VendorsPage() {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [formData, setFormData] = useState({
    name: '', code: '', contact_name: '', email: '', phone: '',
    address: '', terms: 'Net 30', lead_time_days: 0, min_order: '', notes: ''
  });

  const { token = localStorage.getItem('backoffice_token') } = useOutletContext() || {};
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchVendors = useLatestCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/inv/vendors`, { headers });
      if (res.ok) setVendors(await res.json());
    } catch (err) {
      console.error('Error fetching vendors:', err);
    }
    setLoading(false);
  });

  useEffect(() => {
    fetchVendors();
  }, [fetchVendors]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editingVendor 
        ? `${API_URL}/api/inv/vendors/${editingVendor.id}`
        : `${API_URL}/api/inv/vendors`;
      
      const method = editingVendor ? 'PATCH' : 'POST';
      
      const res = await fetch(url, {
        method,
        headers,
        body: JSON.stringify({
          ...formData,
          lead_time_days: parseInt(formData.lead_time_days) || 0,
          min_order: formData.min_order ? parseFloat(formData.min_order) : null
        })
      });
      
      if (res.ok) {
        setShowDialog(false);
        setEditingVendor(null);
        resetForm();
        fetchVendors();
      } else {
        const err = await res.json();
        alert(err.detail || 'Error saving vendor');
      }
    } catch (err) {
      console.error('Error saving vendor:', err);
    }
  };

  const handleEdit = (vendor) => {
    setEditingVendor(vendor);
    setFormData({
      name: vendor.name,
      code: vendor.code || '',
      contact_name: vendor.contact_name || '',
      email: vendor.email || '',
      phone: vendor.phone || '',
      address: vendor.address || '',
      terms: vendor.terms || 'Net 30',
      lead_time_days: vendor.lead_time_days || 0,
      min_order: vendor.min_order || '',
      notes: vendor.notes || ''
    });
    setShowDialog(true);
  };

  const resetForm = () => {
    setFormData({
      name: '', code: '', contact_name: '', email: '', phone: '',
      address: '', terms: 'Net 30', lead_time_days: 0, min_order: '', notes: ''
    });
  };

  const filteredVendors = vendors.filter(v => 
    v.name.toLowerCase().includes(search.toLowerCase()) ||
    (v.code && v.code.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div className="p-6 space-y-6" data-testid="vendors-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <a href={bo('inventory')} className="p-2 hover:bg-gray-800 rounded-lg">
          <ArrowLeft className="w-5 h-5 text-gray-400" />
        </a>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-white">Vendors</h1>
          <p className="text-gray-400">Manage your suppliers and vendors</p>
        </div>
        <Dialog open={showDialog} onOpenChange={(open) => {
          setShowDialog(open);
          if (!open) {
            setEditingVendor(null);
            resetForm();
          }
        }}>
          <DialogTrigger asChild>
            <Button data-testid="add-vendor-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Vendor
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-gray-900 border-gray-700 max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-white">
                {editingVendor ? 'Edit Vendor' : 'Add New Vendor'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label className="text-gray-300">Company Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="bg-gray-800 border-gray-700"
                    placeholder="Vendor name"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Vendor Code</Label>
                  <Input
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                    className="bg-gray-800 border-gray-700"
                    placeholder="VEND-001"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Contact Name</Label>
                  <Input
                    value={formData.contact_name}
                    onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                    className="bg-gray-800 border-gray-700"
                    placeholder="John Doe"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Email</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="bg-gray-800 border-gray-700"
                    placeholder="contact@vendor.com"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Phone</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="bg-gray-800 border-gray-700"
                    placeholder="(555) 123-4567"
                  />
                </div>
                <div className="col-span-2">
                  <Label className="text-gray-300">Address</Label>
                  <Input
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    className="bg-gray-800 border-gray-700"
                    placeholder="123 Main St, City, State"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Payment Terms</Label>
                  <Input
                    value={formData.terms}
                    onChange={(e) => setFormData({ ...formData, terms: e.target.value })}
                    className="bg-gray-800 border-gray-700"
                    placeholder="Net 30"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Lead Time (days)</Label>
                  <Input
                    type="number"
                    value={formData.lead_time_days}
                    onChange={(e) => setFormData({ ...formData, lead_time_days: e.target.value })}
                    className="bg-gray-800 border-gray-700"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Minimum Order ($)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.min_order}
                    onChange={(e) => setFormData({ ...formData, min_order: e.target.value })}
                    className="bg-gray-800 border-gray-700"
                    placeholder="0.00"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit" data-testid="save-vendor-btn">
                  {editingVendor ? 'Update' : 'Create'} Vendor
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          placeholder="Search vendors..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10 bg-gray-800 border-gray-700"
        />
      </div>

      {/* Vendors Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredVendors.map(vendor => (
          <Card key={vendor.id} className="bg-gray-900/50 border-gray-700 hover:border-gray-600 transition-colors">
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-600/20 rounded-lg">
                    <Building className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">{vendor.name}</h3>
                    {vendor.code && (
                      <p className="text-sm text-gray-400">{vendor.code}</p>
                    )}
                  </div>
                </div>
                <Badge className={vendor.enabled ? 'bg-green-600' : 'bg-gray-600'}>
                  {vendor.enabled ? 'Active' : 'Inactive'}
                </Badge>
              </div>
              
              <div className="space-y-2 text-sm">
                {vendor.contact_name && (
                  <p className="text-gray-300">{vendor.contact_name}</p>
                )}
                {vendor.email && (
                  <div className="flex items-center gap-2 text-gray-400">
                    <Mail className="w-4 h-4" />
                    <span>{vendor.email}</span>
                  </div>
                )}
                {vendor.phone && (
                  <div className="flex items-center gap-2 text-gray-400">
                    <Phone className="w-4 h-4" />
                    <span>{vendor.phone}</span>
                  </div>
                )}
                <div className="flex items-center justify-between pt-2 border-t border-gray-700 mt-3">
                  <span className="text-gray-400">Terms: {vendor.terms || '-'}</span>
                  <span className="text-gray-400">Lead: {vendor.lead_time_days} days</span>
                </div>
              </div>
              
              <div className="flex gap-2 mt-4">
                <Button
                  size="sm"
                  variant="outline"
                  className="flex-1"
                  onClick={() => handleEdit(vendor)}
                >
                  <Edit className="w-4 h-4 mr-1" /> Edit
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {filteredVendors.length === 0 && (
          <div className="col-span-full text-center py-12 text-gray-400">
            {loading ? 'Loading...' : 'No vendors found. Add your first vendor!'}
          </div>
        )}
      </div>
    </div>
  );
}
